import React, { useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { api } from '../api/client';
import { useSnackbar } from '../context/SnackbarContext';

const ResetPassword: React.FC = () => {
  const [searchParams] = useSearchParams();
  const token = searchParams.get('token') || '';
  const tokenError = token ? null : 'Missing reset token in the URL.';
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);
  const { showSnackbar } = useSnackbar();

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!token) {
      showSnackbar('Missing reset token in the URL.', { variant: 'error' });
      return;
    }

    if (password !== confirmPassword) {
      showSnackbar('Passwords do not match.', { variant: 'error' });
      return;
    }

    if (password.length < 8) {
      showSnackbar('Password must be at least 8 characters long.', { variant: 'error' });
      return;
    }

    setLoading(true);

    try {
      await api.resetPassword(token, password);
      setSuccess(true);
      setPassword('');
      setConfirmPassword('');
    } catch (err: any) {
      showSnackbar(err.message || 'Password reset failed. Please request a new link.', { variant: 'error' });
    } finally {
      setLoading(false);
    }
  };

  if (success) {
    return (
      <div className="auth-container">
        <div className="auth-box">
          <h1>Password Updated</h1>
          <p className="auth-subtitle">Your password has been reset.</p>
          <p className="auth-link">
            <Link to="/login">Go to login</Link>
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="auth-container">
      <div className="auth-box">
        <h1>Choose New Password</h1>
        <p className="auth-subtitle">Use a password you have not used here before.</p>

        {tokenError && <div className="error">{tokenError}</div>}

        <form onSubmit={handleSubmit} className="auth-form">
          <div className="form-group">
            <label htmlFor="password">Password</label>
            <input
              id="password"
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              placeholder="••••••••"
              required
              autoComplete="new-password"
              minLength={8}
              disabled={!token}
            />
            <small>Must be at least 8 characters</small>
          </div>

          <div className="form-group">
            <label htmlFor="confirmPassword">Confirm Password</label>
            <input
              id="confirmPassword"
              type="password"
              value={confirmPassword}
              onChange={(e) => setConfirmPassword(e.target.value)}
              placeholder="••••••••"
              required
              autoComplete="new-password"
              disabled={!token}
            />
          </div>

          <button type="submit" className="btn btn-primary btn-full" disabled={loading || !token}>
            {loading ? 'Saving...' : 'Reset Password'}
          </button>
        </form>

        <p className="auth-link">
          <Link to="/forgot-password">Request a new link</Link>
        </p>
      </div>
    </div>
  );
};

export default ResetPassword;
